// Determine API URL with production fallback
const getApiBaseUrl = (): string => {
  // Check if we're running on Azure production
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';

  if (hostname === 'creditprocesstool-dev-web.azurewebsites.net') {
    // Hardcoded production API URL for demo
    return 'https://creditprocesstool-dev-api.azurewebsites.net/api/v1';
  }

  const version = import.meta.env.VITE_API_VERSION || 'v1';

  // Use environment variable if explicitly set
  if (import.meta.env.VITE_API_URL) {
    let baseUrl = import.meta.env.VITE_API_URL;
    // Ensure the URL has a protocol (fix common misconfiguration)
    if (!baseUrl.startsWith('http://') && !baseUrl.startsWith('https://')) {
      baseUrl = `https://${baseUrl}`;
    }
    return `${baseUrl.replace(/\/$/, '')}/api/${version}`;
  }

  // In development, use relative URL so Vite proxy handles requests (avoids CORS)
  if (import.meta.env.DEV) {
    return `/api/${version}`;
  }

  // Production fallback - use relative URL so Nginx/reverse proxy handles routing
  return `/api/${version}`;
};

export const API_BASE_URL = getApiBaseUrl();


export const unwrapApiResponse = <T>(result: { data?: T } | T): T => {
  if (
    result &&
    typeof result === 'object' &&
    'data' in result &&
    (result as { data?: T }).data !== undefined
  ) {
    return (result as { data?: T }).data as T;
  }
  return result as T;
};

export interface ApiError {
  statusCode: number;
  message: string;
  error?: string;
  details?: Record<string, unknown>;
}

export class ApiClient {
  private baseUrl: string;
  private accessToken: string | null = null;
  private refreshPromise: Promise<string | null> | null = null;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  setAccessToken(token: string | null) {
    this.accessToken = token;
  }

  getAccessToken() {
    return this.accessToken;
  }

  private resolveToken(): string | false {
    return (
      this.accessToken ||
      (typeof window !== 'undefined' &&
        (localStorage.getItem('accessToken') || localStorage.getItem('token') || false))
    );
  }

  private async request<T>(endpoint: string, options: RequestInit = {}, isRetry = false): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;

    // Belt-and-suspenders: if initializeAuth() was not called or ran before the token
    // was stored (timing edge case in production builds), read directly from localStorage.
    if (!this.accessToken && typeof window !== 'undefined') {
      const stored = localStorage.getItem('accessToken') || localStorage.getItem('token');
      if (stored) this.accessToken = stored;
    }

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    const token = this.resolveToken();
    if (token) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      ...options,
      headers,
      credentials: 'include', // send httpOnly refresh-token cookie on every request
    });

    if (!response.ok) {
      // On 401, attempt a single token refresh + retry before giving up
      if (response.status === 401 && !isRetry) {
        const newToken = await this.attemptTokenRefresh();
        if (newToken) {
          return this.request<T>(endpoint, options, true);
        }

        // Refresh failed — clear auth and notify
        this.clearAuth();
        window.dispatchEvent(new CustomEvent('auth:unauthorized'));
      }

      const body = await response.json().catch(() => ({
        statusCode: response.status,
        message: response.statusText,
      }));

      // Normalise: always include the HTTP status code and extract message
      // from the wrapped { success, error: { code, message } } format
      const error: ApiError = {
        statusCode: body.statusCode ?? response.status,
        message: body.message ?? body.error?.message ?? response.statusText,
        error: body.error?.code ?? body.error,
        details: body.details ?? body.meta,
      };

      throw error;
    }

    // Handle 204 No Content
    if (response.status === 204) {
      return undefined as T;
    }

    return response.json();
  }

  /** Attempt to refresh the access token using the httpOnly refresh-token cookie. Deduplicates concurrent calls. */
  private attemptTokenRefresh(): Promise<string | null> {
    if (this.refreshPromise) return this.refreshPromise;

    this.refreshPromise = (async () => {
      try {
        const res = await fetch(`${this.baseUrl}/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include', // sends httpOnly refreshToken cookie
        });
        if (!res.ok) return null;

        const json = await res.json();
        const data = json.data ?? json;
        if (!data.accessToken) return null;

        this.accessToken = data.accessToken;
        localStorage.setItem('accessToken', data.accessToken);
        return data.accessToken as string;
      } catch {
        return null;
      } finally {
        this.refreshPromise = null;
      }
    })();

    return this.refreshPromise;
  }

  private clearAuth(): void {
    this.accessToken = null;
    if (typeof window !== 'undefined') {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('token');
    }
  }

  get<T>(endpoint: string, params?: Record<string, string | number | boolean>): Promise<T> {
    let url = endpoint;
    if (params) {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, String(value));
        }
      });
      url += `?${searchParams.toString()}`;
    }
    return this.request<T>(url, { method: 'GET' });
  }

  post<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  patch<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  put<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }

  async uploadFile(
    endpoint: string,
    file: File,
    additionalData?: Record<string, string>,
  ): Promise<{ url: string; id: string }> {
    const formData = new FormData();
    formData.append('file', file);

    if (additionalData) {
      Object.entries(additionalData).forEach(([key, value]) => {
        formData.append(key, value);
      });
    }

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'POST',
      headers: this.accessToken ? { Authorization: `Bearer ${this.accessToken}` } : {},
      body: formData,
      credentials: 'include',
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Upload failed' }));
      throw error;
    }

    return response.json();
  }
}

export const apiClient = new ApiClient();
let fetchProxyInstalled = false;

const normalizeApiPath = (input: string) => {
  if (input.startsWith('/api/v1')) {
    return input.replace(/^\/api\/v1/, '') || '/';
  }
  if (input.startsWith('/api/')) {
    return input.replace(/^\/api\//, '/');
  }
  return input;
};

const buildAbsoluteApiUrl = (path: string) => {
  const normalizedBase = API_BASE_URL.replace(/\/$/, '');
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${normalizedBase}${normalizedPath}`;
};

const resolveAuthHeader = () => {
  if (typeof window === 'undefined') {
    return null;
  }
  return (
    apiClient.getAccessToken() ||
    window.localStorage.getItem('accessToken') ||
    window.localStorage.getItem('token')
  );
};

// Helper to handle 401 unauthorized responses from the fetch proxy.
// NOTE: ApiClient.request() has its own 401 handling with refresh+retry.
// This only fires for direct fetch() calls intercepted by the proxy that
// do NOT go through ApiClient (e.g., third-party code). We avoid clearing
// tokens here because a concurrent refresh may have just succeeded.
const handle401Response = (response: Response): Response => {
  return response;
};

export const ensureApiFetchProxy = () => {
  if (fetchProxyInstalled || typeof window === 'undefined' || typeof window.fetch !== 'function') {
    return;
  }

  // When using relative URLs (dev mode with Vite proxy), skip fetch interception
  // The Vite proxy already handles /api/* → backend forwarding
  if (!API_BASE_URL.startsWith('http')) {
    fetchProxyInstalled = true;
    return;
  }

  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    // Only intercept relative paths starting with /api/ (not absolute URLs)
    if (typeof input === 'string' && input.startsWith('/api/') && !input.startsWith('http')) {
      const rewrittenPath = normalizeApiPath(input);
      const absoluteUrl = buildAbsoluteApiUrl(rewrittenPath);

      const headers = new Headers(init?.headers ?? {});
      if (!headers.has('Authorization')) {
        const token = resolveAuthHeader();
        if (token) {
          headers.set('Authorization', `Bearer ${token}`);
        }
      }

      const response = await originalFetch(absoluteUrl, {
        ...init,
        headers,
      });

      return handle401Response(response);
    }

    // Also handle 401 for API calls that are already absolute URLs
    const inputStr = typeof input === 'string' ? input : input.toString();
    if (inputStr.includes('/api/')) {
      const response = await originalFetch(input, init);
      return handle401Response(response);
    }

    return originalFetch(input, init);
  };

  fetchProxyInstalled = true;
};
